/**
 * FlowUp — Gender preference (Hebrew grammar) singleton.
 *
 * Lets every component address the user in their preferred grammatical
 * form ("male"/"female"/"neutral").  Persisted in localStorage so the
 * choice survives reloads + can be set BEFORE the user even registers
 * (we capture it from the modal that pops on the homepage CTA click).
 */
import { useEffect, useState, useCallback } from "react";

const KEY = "flowup_gender";

export function readGender() {
  try { return localStorage.getItem(KEY) || ""; }
  catch { return ""; }
}

export function writeGender(g) {
  try {
    if (g) localStorage.setItem(KEY, g);
    else localStorage.removeItem(KEY);
    window.dispatchEvent(new CustomEvent("flowup-gender-change", { detail: g }));
  } catch { /* no-op */ }
}

/**
 * `useGender` — reactive hook.
 *   const [gender, setGender, t] = useGender();
 *   t("ברוך הבא", "ברוכה הבאה")  // picks form based on user's pick
 */
export function useGender() {
  const [gender, _set] = useState(() => readGender());

  useEffect(() => {
    const h = (e) => _set(e.detail || readGender());
    window.addEventListener("flowup-gender-change", h);
    window.addEventListener("storage", () => _set(readGender()));
    return () => window.removeEventListener("flowup-gender-change", h);
  }, []);

  const set = useCallback((g) => { writeGender(g); _set(g || ""); }, []);

  const t = useCallback((male, female, neutral) => {
    if (gender === "female") return female;
    if (gender === "male") return male;
    return neutral || male;
  }, [gender]);

  return [gender, set, t];
}
